import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
} from '@/components/ui/dropdown-menu';
import {
  Video, Radio, ImageIcon, MoreVertical, Eye, Pencil, Archive, ArchiveRestore,
  Trash2, FolderOpen, Cloud, Film,
} from 'lucide-react';

const KIND_ICON = { video: Video, live: Radio, replay: Radio, design: ImageIcon };

const KIND_LABEL = {
  video: 'Video', live: 'Live', replay: 'Replay',
  thumbnail: 'Thumbnail', image_post: 'Image Post', carousel: 'Carousel', story: 'Story', promo_post: 'Post',
};

// A single asset tile shared by real, backend-tracked video/live/replay
// assets AND browser-local design assets (thumbnails, image posts,
// carousels, Stories). `item` is pre-normalized by the caller so this
// component never has to branch on where the data actually came from.
export function AssetGridCard({
  item, onPreview, onRename, onArchiveToggle, onDelete, onEdit, onBackup,
  folders = [], folderIds = [], onToggleFolder,
}) {
  const Icon = KIND_ICON[item.kind] || Video;
  const typeLabel = KIND_LABEL[item.assetKind] || KIND_LABEL[item.kind] || item.kind;
  const folderSet = new Set(folderIds);

  return (
    <div
      className="group relative rounded-lg bg-white/5 border border-white/10 overflow-hidden hover:border-white/20 transition-colors"
      data-testid={`library-card-${item.id}`}
    >
      <button
        type="button"
        className="w-full aspect-video bg-black/40 flex items-center justify-center overflow-hidden"
        onClick={() => onPreview(item)}
        data-testid={`library-card-preview-${item.id}`}
      >
        {item.thumbnailUrl ? (
          <img src={item.thumbnailUrl} alt="" className="w-full h-full object-cover" />
        ) : (
          <Icon className="h-8 w-8 text-brand-text-muted" />
        )}
        {item.childCount > 0 && (
          <Badge className="absolute top-2 left-2 bg-black/60 border-white/20 text-white text-[10px] flex items-center gap-1">
            <Film className="h-3 w-3" />{item.childCount + 1} videos
          </Badge>
        )}
        {item.localOnly && (
          <Badge className="absolute top-2 right-2 bg-black/60 border-white/20 text-[#C0C7D4] text-[10px]">
            Saved in this browser
          </Badge>
        )}
      </button>

      <div className="p-3 flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-white truncate" data-testid={`library-card-name-${item.id}`}>{item.displayName}</p>
          <div className="flex items-center gap-1.5 mt-1 flex-wrap">
            <Badge variant="outline" className="border-white/20 text-[#8A93A6] text-[10px] px-1.5 py-0">{typeLabel}</Badge>
            {item.projectName && (
              <Badge variant="outline" className="border-white/20 text-[#8A93A6] text-[10px] px-1.5 py-0">{item.projectName}</Badge>
            )}
            {item.statusBadge}
          </div>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="icon" variant="outline" className="border-white/10 h-7 w-7 shrink-0" data-testid={`library-card-menu-${item.id}`}>
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onPreview(item)} data-testid={`library-card-action-preview-${item.id}`}>
              <Eye className="h-4 w-4 mr-2" />Preview
            </DropdownMenuItem>
            {!item.localOnly && onEdit && (
              <DropdownMenuItem onClick={() => onEdit(item)} data-testid={`library-card-action-edit-${item.id}`}>
                <Pencil className="h-4 w-4 mr-2" />Edit
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={() => {
                const next = window.prompt('Rename', item.displayName);
                if (next && next.trim()) onRename(item, next.trim());
              }}
              data-testid={`library-card-action-rename-${item.id}`}
            >
              <Pencil className="h-4 w-4 mr-2" />Rename
            </DropdownMenuItem>

            {folders.length > 0 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger data-testid={`library-card-action-folder-${item.id}`}>
                  <FolderOpen className="h-4 w-4 mr-2" />Folders
                </DropdownMenuSubTrigger>
                <DropdownMenuSubContent>
                  {folders.map((f) => (
                    <DropdownMenuItem
                      key={f.id}
                      onClick={() => onToggleFolder(item, f.id, !folderSet.has(f.id))}
                      data-testid={`library-card-folder-toggle-${item.id}-${f.id}`}
                    >
                      {folderSet.has(f.id) ? '✓ ' : ''}{f.name}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuSubContent>
              </DropdownMenuSub>
            )}

            {!item.localOnly && onBackup && (
              <DropdownMenuItem onClick={() => onBackup(item)} data-testid={`library-card-action-backup-${item.id}`}>
                <Cloud className="h-4 w-4 mr-2" />Back Up to Dropbox
              </DropdownMenuItem>
            )}

            <DropdownMenuItem onClick={() => onArchiveToggle(item, !item.isArchived)} data-testid={`library-card-action-archive-${item.id}`}>
              {item.isArchived ? <ArchiveRestore className="h-4 w-4 mr-2" /> : <Archive className="h-4 w-4 mr-2" />}
              {item.isArchived ? 'Restore' : 'Archive'}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem className="text-red-400 focus:text-red-400" onClick={() => onDelete(item)} data-testid={`library-card-action-delete-${item.id}`}>
              <Trash2 className="h-4 w-4 mr-2" />Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}
